define('webshopAttributesValueAutocomplete',[
  'jquery',
  'underscore',
  'entities/webshop/attribute_matching',
  'jqueryui'
], function (
  $,
  _,
  MatchingApi
) {
  'use strict';

  var SUGGESTIONS = 5;
  var SUGGESTION_MIN_SCORE = 0.6;
  var LIST_LIMIT = 60;

  // Lower case, no accents, punctuation as spaces: "Coton bio (180 g/m²)" → "coton bio 180 g m2".
  var normalize = function (text) {
    return (text || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
      .replace(/²/g, '2').replace(/[^a-z0-9]+/g, ' ').trim();
  };

  var levenshtein = function (a, b) {
    var prev = [], cur, i, j;
    for (j = 0; j <= b.length; j++)
      prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur = [i];
      for (j = 1; j <= b.length; j++)
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1));
      prev = cur;
    }
    return prev[b.length];
  };

  // How well a text matches the typed query (both normalized): 0 same text, 1 starts with it,
  // 2 a word starts with it, 3 contains it, null no match.
  var matchRank = function (text, query) {
    if (!query)
      return 3;
    if (text === query)
      return 0;
    if (text.indexOf(query) === 0)
      return 1;
    if ((' ' + text).indexOf(' ' + query) !== -1)
      return 2;
    return text.indexOf(query) !== -1 ? 3 : null;
  };

  // 0..1: same text 1, one containing the other 0.85, else the best of spelling and shared words.
  var similarity = function (a, b) {
    a = normalize(a);
    b = normalize(b);
    if (!a || !b)
      return 0;
    if (a === b)
      return 1;
    var shorter = a.length < b.length ? a : b;
    var longer = a.length < b.length ? b : a;
    if (shorter.length >= 3 && (' ' + longer + ' ').indexOf(' ' + shorter + ' ') !== -1)
      return 0.85;
    var spelling = 1 - levenshtein(a, b) / Math.max(a.length, b.length);
    var wa = a.split(' '), wb = b.split(' ');
    var common = _.intersection(wa, wb).length;
    var words = common ? common / _.union(wa, wb).length : 0;
    if (shorter.length >= 3 && longer.indexOf(shorter) !== -1)
      words = Math.max(words, 0.7);
    return Math.max(spelling, words);
  };

  // Best similarity between the reference texts (supplier value label, code) and a canonical
  // value (its code and its name in every language).
  var score = function (references, value) {
    var texts = [value.code].concat(_.values(value.names || {}));
    var best = 0;
    _.each(references, function (r) {
      _.each(texts, function (t) {
        best = Math.max(best, similarity(r, t));
      });
    });
    return best;
  };

  var supplierValues = function (n) {
    n = n || 0;
    return n + ' ' + (n > 1 ? _.i18n('attributeMatching.supplierValuesShort', null, 'supplier values') :
      _.i18n('attributeMatching.supplierValueShort', null, 'supplier value'));
  };

  // Supplier values linked to the value the way the cell links them: count 'attr' (shown as),
  // 'filter' (as a filter, any of Filter 1..3) or 'both' (the two numbers).
  var counts = function (value, count) {
    if (count === 'attr')
      return {
        text: supplierValues(value.attributeMatchCount),
        title: _.i18n('attributeMatching.countAttrTitle', null, 'Supplier values shown as this value')
      };
    if (count === 'filter')
      return {
        text: supplierValues(value.filterMatchCount),
        title: _.i18n('attributeMatching.countFilterTitle', null, 'Supplier values linked to this value as a filter (Filter 1, 2 or 3)')
      };
    return {
      text: (value.attributeMatchCount || 0) + ' ' + _.i18n('attributeMatching.shownAsShort', null, 'shown as') + ' · ' +
        (value.filterMatchCount || 0) + ' ' + _.i18n('attributeMatching.filtersShort', null, 'as filter'),
      title: _.i18n('attributeMatching.countBothTitle', null, 'Supplier values shown as this value · linked to it as a filter')
    };
  };

  // jQuery UI autocomplete grouped in sections (suggestions / values / actions).
  $.widget('compuzz.amValueComplete', $.ui.autocomplete, {
    _create: function () {
      this._super();
      this.widget().menu('option', 'items', '> :not(.am-ac-section)');
    },

    _renderMenu: function (ul, items) {
      var that = this;
      var section = null;
      ul.addClass('am-ac-menu');
      _.each(items, function (item) {
        if (item.section !== section) {
          section = item.section;
          if (item.sectionTitle)
            $('<li class="am-ac-section"></li>').text(item.sectionTitle).appendTo(ul);
        }
        that._renderItemData(ul, item);
      });
    },

    _renderItem: function (ul, item) {
      var li = $('<li></li>').addClass('am-ac-item ' + (item.kind || ''));
      var line = $('<div></div>').appendTo(li);
      if (item.color)
        $('<span class="am-swatch"></span>').css('background', item.color).appendTo(line);
      $('<span class="am-ac-name"></span>').text(item.label).appendTo(line);
      if (item.counts)
        $('<span class="am-ac-counts"></span>').text(item.counts).attr('title', item.countsTitle).appendTo(line);
      return li.appendTo(ul);
    }
  });

  /**
   * Turns an input into the canonical value picker of a supplier value cell.
   * options:
   *   values()      canonical values of the attribute (current list)
   *   references    texts the suggestions are computed against (supplier value label / code), or a
   *                 function returning them; may be empty
   *   currentId()   id of the value currently chosen, or null
   *   onPick(id)    a value was chosen (null: matching removed)
   *   onCreate(txt) "Create" was chosen
   *   count         number shown next to each value: 'attr' (shown as), 'filter' (as a filter)
   *                 or 'both' (default)
   */
  var attach = function ($input, options) {
    var labelOf = function (id) {
      return id ? MatchingApi.labels(options.values())[id] || '' : '';
    };

    var item = function (value, labels, section, sectionTitle) {
      var c = counts(value, options.count);
      return {
        id: value.id,
        label: labels[value.id],
        value: labels[value.id],
        color: MatchingApi.color(value),
        counts: c.text,
        countsTitle: c.title,
        section: section,
        sectionTitle: sectionTitle
      };
    };

    $input.amValueComplete({
      minLength: 0,
      delay: 100,
      appendTo: 'body',
      position: {my: 'left top', at: 'left bottom', collision: 'flip'},
      source: function (request, response) {
        var values = options.values();
        var labels = MatchingApi.labels(values);
        var currentLabel = labelOf(options.currentId());
        var term = (request.term || '').trim();
        var browsing = !term || term === currentLabel;
        var items = [];

        var query = normalize(term);
        var references = _.compact((_.isFunction(options.references) ? options.references() : options.references) || []);
        // Best rank of the value's label, code and names in every language against the typed text
        var rankOf = function (v) {
          var ranks = _.filter(_.map([labels[v.id], v.code].concat(_.values(v.names || {})), function (t) {
            return matchRank(normalize(t), query);
          }), function (r) { return r !== null; });
          return ranks.length ? _.min(ranks) : null;
        };

        if (browsing) {
          // 1. Values that match (or nearly) the supplier value
          if (references.length) {
            var suggested = _.chain(values)
              .map(function (v) { return {value: v, score: score(references, v)}; })
              .filter(function (s) { return s.score >= SUGGESTION_MIN_SCORE; })
              .sortBy(function (s) { return -s.score; })
              .first(SUGGESTIONS)
              .value();
            _.each(suggested, function (s) {
              items.push(item(s.value, labels, 'suggestions', _.i18n('attributeMatching.suggestions', null, 'Suggestions')));
            });
          }
          // 2. Every other value
          var shown = _.pluck(items, 'id');
          var others = _.reject(values, function (v) { return _.contains(shown, v.id); });
          _.each(_.first(_.sortBy(others, function (v) { return labels[v.id].toLowerCase(); }), LIST_LIMIT), function (v) {
            items.push(item(v, labels, 'values', items.length ? _.i18n('attributeMatching.allValues', null, 'Values') : null));
          });
        } else {
          // Typing: one list, the full match first (then starts with, a word starts with, contains);
          // same rank: the values close to the supplier value first, then alphabetical.
          var ranked = _.chain(values)
            .map(function (v) {
              return {value: v, rank: rankOf(v), score: references.length ? score(references, v) : 0};
            })
            .filter(function (r) { return r.rank !== null; })
            .sortBy(function (r) { return labels[r.value.id].toLowerCase(); })
            .sortBy(function (r) { return r.score >= SUGGESTION_MIN_SCORE ? -r.score : 0; })
            .sortBy('rank')
            .first(LIST_LIMIT)
            .value();
          _.each(ranked, function (r) {
            var it = item(r.value, labels, 'values', null);
            if (r.rank === 0)
              it.kind = 'exact';
            items.push(it);
          });
        }

        // 3. Actions
        var exact = _.find(values, function (v) {
          return normalize(v.code) === query || normalize(MatchingApi.name(v)) === query;
        });
        if (!browsing && term && !exact && options.onCreate)
          items.push({id: null, create: term, label: _.i18n('attributeMatching.createValueNamed', null, 'Create "%s"').replace('%s', term),
            value: term, kind: 'create', section: 'actions'});
        if (options.currentId() && !options.noClear)
          items.push({id: null, clear: true, label: _.i18n('attributeMatching.clearMatching', null, '— No matching'), value: '',
            kind: 'clear', section: 'actions'});

        response(items);
      },
      select: function (e, ui) {
        e.preventDefault();
        if (ui.item.create) {
          options.onCreate(ui.item.create);
        } else {
          $input.val(ui.item.clear ? '' : ui.item.value);
          options.onPick(ui.item.clear ? null : ui.item.id);
        }
        $input.blur();
      },
      focus: function (e) {
        // keep the typed text while moving in the list
        e.preventDefault();
      },
      change: function (e, ui) {
        // left without choosing: back to the current value
        if (!ui.item)
          $input.val(labelOf(options.currentId()));
      }
    });

    $input.on('focus.amValueComplete', function () {
      $input.select();
      $input.amValueComplete('search', '');
    });
  };

  return {
    attach: attach,
    similarity: similarity
  };
});

